import { HeroBannerPropsModel } from "@models/hero-banner";
import React from "react";
import Button from "../Button";
import BreadCrumbs from "../BreadCrumbs";
import ImageRenderer from "../Image";
import { twMerge } from "tailwind-merge";
import NavItem from "../Layout/NavItem";
import { Variants, motion } from "framer-motion";
import { isEmpty } from "@utils/common";
import Link from "../Link";
import Title from "../Title";

const wrapperVariant: Variants = {
  end: {
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const imageVariant: Variants = {
  start: {
    opacity: 0,
  },
  end: {
    opacity: 1,
  },
};

const HeroBanner: React.FunctionComponent<HeroBannerPropsModel> = ({
  title,
  bigHeader,
  description,
  ctaBtnText,
  ctaBtnLink,
  topbanner: image,
  path,
  appendToPath = "",
  imageClassName = "",
  secondaryNav,
  author,
  titleClassName = "",
  bigHeaderClassName = "",
  descriptionClassName = "",
}) => {
  const mainHeaderDataObjects = secondaryNav?.items?.data ?? [];

  const renderImage = () => {
    if (!image) return <></>;
    return (
      <motion.div variants={imageVariant} className="hidden xl:block">
        <ImageRenderer image={image} />;
      </motion.div>
    );
  };

  const renderTextAndButton = () => {
    let dynamicClassName =
      author && !isEmpty(author)
        ? "mb-8 md:mb-[50px]"
        : "mb-8 md:mb-[50px] xl:mb-[95px]";

    let localTitleClassName = "";
    let localTitleWrapperClassName = "";

    if (!image?.localFile) {
      dynamicClassName += " !w-full justify-center";
      localTitleClassName += " xl:text-center";
      localTitleWrapperClassName = " xl:items-center";
    }

    return (
      <div
        className={twMerge(
          "md:mt-[38px] xl:flex justify-between relative gap-16",
          dynamicClassName
        )}
      >
        <div
          className={twMerge(
            "flex flex-col items-center xl:items-start",
            localTitleWrapperClassName
          )}
        >
          <motion.div variants={imageVariant}>
            <Title
              variant="h3"
              titleText={title}
              className={twMerge(
                "text-primary-orange-50 text-center xl:text-left",
                titleClassName,
                localTitleClassName
              )}
            />
            <div
              className={twMerge(
                "text-center xl:text-left text-4xl md:text-[56px] xl:text-6xl 2xl:text-7xl leading-[40px] md:leading-[56px] 2xl:leading-[76px] text-white font-medium max-w-[720px] xl:max-w-[649px]",
                bigHeaderClassName,
                localTitleClassName
              )}
            >
              {bigHeader}
            </div>
          </motion.div>
          <motion.div variants={imageVariant}>
            <Title
              variant="paragraph"
              titleText={description}
              className={twMerge(
                "text-white mt-[22px] max-w-[582px] text-center xl:text-left",
                descriptionClassName,
                localTitleClassName
              )}
            />
          </motion.div>
          {ctaBtnText?.length && ctaBtnLink?.length && (
            <motion.div className="mt-8 xl:mt-12" variants={imageVariant}>
              <Button variant="primary" className="!p-0 !h-auto">
                <Link
                  className="no-underline hover:font-normal px-10 py-3 block"
                  href={ctaBtnLink}
                  linkText={ctaBtnText}
                  style={{
                    color: "inherit",
                    fontSize: "inherit",
                  }}
                />
              </Button>
            </motion.div>
          )}
          {renderAuthor(
            "block xl:hidden mb-0 pt-8 pb-0 w-full flex justify-center"
          )}
        </div>
        <div
          className={twMerge(
            "mt-8 xl:mt-3.5 xl:shrink-0 flex items-center justify-center xl:justify-end",
            imageClassName
          )}
        >
          {renderImage()}
        </div>
      </div>
    );
  };

  const renderSecondaryNavigation = () => {
    return (
      <ul className="flex gap-8 relative">
        {mainHeaderDataObjects?.map((oneMainHeaderDataObject) => {
          const { attributes } = oneMainHeaderDataObject ?? {};
          return (
            <NavItem
              navAttributes={attributes}
              key={oneMainHeaderDataObject.id}
              topOffset={28}
              showBanner={false}
            />
          );
        })}
      </ul>
    );
  };

  const renderAuthor = (styleName = "") => {
    if (!author || isEmpty(author)) return <></>;
    const {
      firstName = "",
      lastName = "",
      profilePicture,
      about = "",
      // subTitle = "",
    } = author;
    return (
      <motion.div className={twMerge("pb-20 w-[83.25%] mx-auto", styleName)}>
        <div className="flex text-white">
          <div className="w-16 h-16 rounded-full mr-3">
            <ImageRenderer
              image={profilePicture}
              alt={`${firstName} ${lastName}`}
            />
          </div>
          <div>
            <div className="w-[127px] h-[25px] text-white text-lg font-semibold leading-[48px]">
              {firstName + " " + lastName}
            </div>
            <div className="w-[143px] h-[23px] text-white text-base font-normal leading-[48px]">
              {about}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <motion.div
      className="bg-dark-purple-50 pt-4 z-0"
      initial="start"
      animate="end"
      variants={wrapperVariant}
    >
      <motion.div
        className="text-white px-4 md:px-8 lg:px-14 xl:px-[108px] flex justify-between"
        variants={imageVariant}
      >
        <BreadCrumbs path={path} appendToPath={appendToPath} />
        <div className="hidden xl:flex">{renderSecondaryNavigation()}</div>
      </motion.div>
      <div className="w-full flex justify-center overflow-hidden">
        <div className="w-[83.25%] max-w-[1440px]">{renderTextAndButton()}</div>
      </div>
      {renderAuthor("hidden xl:block")}
    </motion.div>
  );
};
export default HeroBanner;
